import './transactionHistoryPage.css';
import React, {Component} from "react";
import UserDetailsService from "../services/userDetailsService";
import {Navigate} from "react-router-dom";
import RedirectionService from "../services/redirectionService";
import {TransactionSummaryDto, UserDto, BasicUserDto} from "../model/functionModels";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import TransactionList, {DisplayMode} from "./transactionList";
import SummaryService from "../services/summaryService";
import TransactionService from "../services/transactionService";
import {Multiselect} from "multiselect-react-dropdown";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface TransactionHistoryState {
    navigation?: string,
    loggedInUser?: UserDto,
    allUsers?: (UserDto | BasicUserDto)[],
    summary?: TransactionSummaryDto,
    filterUserIds: string[], // Array of user IDs to filter by (empty = show all)
}

interface TransactionHistoryProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
    onLogout: any,
}

class TransactionHistoryPage extends Component<TransactionHistoryProps, TransactionHistoryState> {
    private unsubscribe?: () => void;

    constructor(props: TransactionHistoryProps) {
        super(props, {})
        this.state = {
            navigation: undefined,
            loggedInUser: undefined,
            allUsers: undefined,
            summary: undefined,
            filterUserIds: [],
        };
        this.handleFailure = this.handleFailure.bind(this);
        this.handleRedirection = this.handleRedirection.bind(this);
        this.refreshAll = this.refreshAll.bind(this);
        this.handleDeleteTransaction = this.handleDeleteTransaction.bind(this);
    }

    async componentDidMount() {
        // Subscribe to locale changes to trigger re-render
        this.unsubscribe = i18nService.subscribe(() => {
            this.forceUpdate();
        });
        
        if (!this.state.navigation) {
            await this.refreshAll();
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    async refreshAll() {
        // Parallelize API calls for better performance
        const promises = [
            UserDetailsService.getOwnUserDetails(async userDto => {
                this.setState({
                    loggedInUser: userDto,
                });
            }, async () => {
                // @ts-ignore
                const currentPath = this.props.location?.pathname || '/history';
                this.setState({
                    navigation: RedirectionService.redirectToLogin(currentPath),
                })
            },
            this.handleRedirection),
            UserDetailsService.getAllUsersList(async users => {
                this.setState({
                    allUsers: users,
                })
            }, this.handleFailure, this.handleRedirection),
            SummaryService.getMySummary(async summary => {
                this.setState({
                    summary: summary,
                });
            }, this.handleFailure, this.handleRedirection)
        ];
        
        // Wait for all API calls to complete in parallel
        await Promise.all(promises);
    }

    async handleRedirection(redirectionUrl: string) {
        if (redirectionUrl.startsWith("/login")) {
            this.props.onLogout();
        }
        this.setState({
            navigation: redirectionUrl
        });
    }

    async handleFailure(error: string) {
        this.props.toastGenerator(error, ToastErrorLevel.ERROR);
    }

    async handleDeleteTransaction(transactionId: string) {
        if (!window.confirm(i18nService.t('history.deleteTransactionConfirm'))) {
            return;
        }
        await TransactionService.deleteTransaction(
            transactionId,
            async () => {
                this.props.toastGenerator(i18nService.t('history.transactionDeleted'), ToastErrorLevel.INFO);
                await this.refreshAll();
            },
            this.handleFailure,
            this.handleRedirection
        );
    }

    getFriendDtos(): (UserDto | BasicUserDto)[] {
        if (!this.state.loggedInUser || !this.state.allUsers) {
            return [];
        }
        return this.state.allUsers.filter(u => u.id !== this.state.loggedInUser!.id && this.state.loggedInUser!.friends.includes(u.id));
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>
        }
        if (!this.state.loggedInUser || !this.state.summary || !this.state.allUsers) {
            return <div className="TransactionHistoryPage-Loading">
                <LoadingSpinner size="large" message={i18nService.t('common.loading')} />
            </div>
        }
        const friends = this.getFriendDtos();
        const involvedUsers = [this.state.loggedInUser, ...friends];
        
        const filterOptions = involvedUsers.map((u) => ({
            id: u.id,
            name: `${u.firstName} ${u.lastName}`,
        }));
        const selectedFilterValues = filterOptions.filter((o) => this.state.filterUserIds.includes(o.id));

        const filteredExpenses = this.state.filterUserIds.length > 0
            ? this.state.summary.expenses.filter((expense) =>
                this.state.filterUserIds.includes(expense.authorUserId) ||
                expense.targets.some((target) => this.state.filterUserIds.includes(target))
            )
            : this.state.summary.expenses;
        
        const filteredPayments = this.state.filterUserIds.length > 0
            ? this.state.summary.payments.filter((payment) =>
                this.state.filterUserIds.includes(payment.authorUserId) ||
                this.state.filterUserIds.includes(payment.userPaidTo)
            )
            : this.state.summary.payments;
        
        const hasTransactions = (filteredExpenses.length > 0 || filteredPayments.length > 0);

        return <div className="TransactionHistoryPage-Wrapper">
            <div className="TransactionHistoryPage-Container">
                <h1 className="TransactionHistoryPage-Title">{i18nService.t('history.title')}</h1>

                <div className="TransactionHistoryPage-Section">
                    <div className="TransactionHistoryPage-FilterHeader">
                        <h2 className="TransactionHistoryPage-SectionTitle">{i18nService.t('history.filter')}</h2>
                        <button
                            className="TransactionHistoryPage-FilterClearBtn"
                            onClick={() => this.setState({filterUserIds: []})}
                            disabled={this.state.filterUserIds.length === 0}
                        >
                            {i18nService.t('history.clear')}
                        </button>
                    </div>
                    <p className="TransactionHistoryPage-FilterHint">{i18nService.t('history.filterHint')}</p>
                    <div className="TransactionHistoryPage-FilterBar">
                        <Multiselect
                            options={filterOptions}
                            selectedValues={selectedFilterValues}
                            displayValue="name"
                            placeholder={i18nService.t('history.filterByPeople')}
                            onSelect={(selectedList: any[]) => {
                                this.setState({filterUserIds: selectedList.map((s) => s.id)});
                            }}
                            onRemove={(selectedList: any[]) => {
                                this.setState({filterUserIds: selectedList.map((s) => s.id)});
                            }}
                            avoidHighlightFirstOption={true}
                            showCheckbox={true}
                        />
                    </div>
                </div>

                {!hasTransactions ? (
                    <div className="TransactionHistoryPage-Section">
                        <p className="TransactionHistoryPage-EmptyState">
                            {this.state.filterUserIds.length > 0 
                                ? i18nService.t('history.noTransactionsFiltered')
                                : i18nService.t('history.noTransactions')}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="TransactionHistoryPage-Section">
                            <h2 className="TransactionHistoryPage-SectionTitle">{i18nService.t('history.expenses')}</h2>
                            {filteredExpenses.length === 0 ? (
                                <p className="TransactionHistoryPage-EmptyState">{i18nService.t('history.noExpensesFiltered')}</p>
                            ) : (
                                <TransactionList 
                                    title="" 
                                    transactions={filteredExpenses} 
                                    involvedUsers={involvedUsers} 
                                    showDetails={true} 
                                    displayMode={DisplayMode.EXPENSE}
                                    loggedInUserId={this.state.loggedInUser.id}
                                    onDeleteTransaction={this.handleDeleteTransaction}
                                />
                            )}
                        </div>

                        <div className="TransactionHistoryPage-Section">
                            <h2 className="TransactionHistoryPage-SectionTitle">{i18nService.t('history.payments')}</h2>
                            {filteredPayments.length === 0 ? (
                                <p className="TransactionHistoryPage-EmptyState">{i18nService.t('history.noPaymentsFiltered')}</p>
                            ) : (
                                <TransactionList 
                                    title="" 
                                    transactions={filteredPayments} 
                                    involvedUsers={involvedUsers} 
                                    showDetails={false} 
                                    displayMode={DisplayMode.PAYMENT}
                                    loggedInUserId={this.state.loggedInUser.id}
                                    onDeleteTransaction={this.handleDeleteTransaction}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    }
}

export default withRouter(TransactionHistoryPage);

